import { Avatar } from "@fidget/common/components/avatar/avatar";
import { FuzzyTime } from "@fidget/common/components/chat/FuzzyTime";
import { TooltipComponent } from "@fidget/common/components/tooltipComponent";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { ChatItemGroup } from "@fidget/common/models/chat/chatItemGroup";
import { ChatItemStatus, IGroupableItem } from "@fidget/common/models/chat/chatItems";
import { equals, UserProfile } from "@fidget/common/models/userProfile";
import { UserPresenter } from "@fidget/common/utils/userPresenter";
import classNames from "classnames";
import { observer } from "mobx-react";
import React from "react";
import { userActions } from "../../actions/user";
import { useForeContext, useStore } from "../../models";
import { renderItem, shouldAlignRightInWidget } from "./chatItemHelper";
import styles from "./chatSection.css";

interface Props {
  item: IGroupableItem;
  currentUser: UserProfile;
  onContactProvided?: (contact: string) => any;
  scrollToBottom?: () => void;
  isMobile?: boolean;
}

export const ChatSection = observer(
  ({ item, currentUser, onContactProvided, scrollToBottom, isMobile }: Props) => {
    const { uiStore } = useStore();
    const context = useForeContext();
    const phoneRegion = context.state.shared.orgConfig?.phoneRegion || "ZZ";

    const userPresenter = UserPresenter.from(item.user);
    const agentDisplayedName = context.state.shared.orgConfig?.showAdviserLastNameEnabled
      ? userPresenter.displayedName
      : userPresenter.firstName;

    const render = () => {
      const own = equals(item.user, currentUser);
      const alignRight = shouldAlignRightInWidget(item.user, currentUser);

      return (
        <div
          className={classNames(styles.section, styles.widget, {
            [styles["align-right"]]: alignRight
          })}
        >
          <div className={styles.content}>
            {!own && (
              <>
                <TooltipComponent
                  positionPreset={"top"}
                  text={agentDisplayedName}
                  style={"widget"}
                  maxWidth={"80px"}
                  changeTransformTooltip={"translateX(-40%)"}
                  changeTransformOutset={"translateX(0)"}
                >
                  <div className={styles.avatar}>
                    <Avatar user={item.user} widgetStyle />
                  </div>
                </TooltipComponent>
                <div className={styles.space} />
              </>
            )}
            <div className={styles.items}>{renderBody()}</div>
          </div>
          {renderTimeAndStatus(own)}
        </div>
      );
    };

    const renderTimeAndStatus = (own: boolean) => {
      if (item.status === ChatItemStatus.UNDELIVERED) {
        const onClick = () =>
          uiStore.setChatNotification({
            message: intl.formatMessage("conversation.error.couldntSend.title"),
            onAction: () => userActions.resendUndelievered(item.id, context),
            actionText: intl.formatMessage("conversation.error.couldntSend.action"),
            onCancel: () => userActions.removeUndelivered(item.id, context)
          });

        return (
          <div
            className={classNames(styles["time-and-status"], styles["status-error"])}
            onClick={onClick}
          >
            <i className="icon-exclamation-mark" />
            {intl.formatMessage("conversation.messageStatus.undelivered")}
          </div>
        );
      } else if (!!item.timestamp) {
        const statusLabel = item.status && getMessageStatusLabel(item.status);

        return (
          <div className={styles["time-and-status"]}>
            <FuzzyTime timestamp={item.timestamp} />
            {own && <span className={styles.status}>{statusLabel}</span>}
          </div>
        );
      }
      return undefined;
    };

    const renderBody = (): React.ReactNode => {
      if (ChatItemGroup.isChatItemGroup(item)) {
        return item.items.map((groupItem) =>
          renderItem(
            groupItem,
            currentUser,
            phoneRegion,
            onContactProvided,
            scrollToBottom,
            isMobile
          )
        );
      } else {
        return renderItem(
          item,
          currentUser,
          phoneRegion,
          onContactProvided,
          scrollToBottom,
          isMobile
        );
      }
    };

    const getMessageStatusLabel = (status: ChatItemStatus): string => {
      switch (status) {
        case ChatItemStatus.SENDING:
          return intl.formatMessage("conversation.messageStatus.sending");
        case ChatItemStatus.SENT:
          return intl.formatMessage("conversation.messageStatus.sent");
        case ChatItemStatus.DELIVERED:
          return intl.formatMessage("conversation.messageStatus.delivered");
        case ChatItemStatus.READ:
          return intl.formatMessage("conversation.messageStatus.read");
        default:
          throw new Error("unsupported message status: " + status);
      }
    };

    return render();
  }
);
